import { ChangeEvent, useContext, useState } from 'react';
import {
  ApprovalReasonsType,
  PricingSheetState,
} from '../../../../../shared/types';
import { createApprovalRequest } from '../../../services/approvalRequests';
import { updatePricingSheetById } from '../../../services/pricingSheets';
import { isValidEmail } from '../../../utils/isValidEmail';
import { Box } from '@mui/system';
import {
  Button,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import MultiLineComment from '../../Common/MultiLineComment';
import { useHistory } from 'react-router-dom';
import NotificationProvider from '../../../providers/Notification/NotificationProvider';
import { CorporateTitle } from '../../../helpers/approvalValidatorCommon';

export const requiredActionLabels = {
  [CorporateTitle.AccountManager]:
    'Hinnasto vaatii hyväksynnän asiakkuuspäälliköltä!',
  [CorporateTitle.SalesManager]:
    'Hinnasto vaatii hyväksynnän myyntipäälliköltä!',
  [CorporateTitle.RegionalManager]:
    'Hinnasto vaatii hyväksynnän aluepäälliköltä!',
  [CorporateTitle.SalesDirector]:
    'Hinnasto vaatii hyväksynnän myyntijohtajalta!',
  [CorporateTitle.CEO]:
    'Hinnasto vaatii hyväksynnän toimitusjohtajalta!',
};

type RequireApprivalProps = {
  pricingSheetId: string;
  requiredApprovalLevel: CorporateTitle;
};

export const RequireApproval = ({
  pricingSheetId,
  requiredApprovalLevel,
}: RequireApprivalProps) => {
  let history = useHistory();
  const { setNotification } = useContext(NotificationProvider);

  const [email, setEmail] = useState('');
  const [comment, setComment] = useState<string | undefined>(
    undefined,
  );

  const handleEmailChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setEmail(event.target.value);
  };

  const handleCommentChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setComment(event.target.value);
  };

  const approvalReasons: ApprovalReasonsType = {
    // red rows
    belowOneCoefficient: {
      count: 0,
      requireApproval: false,
    },
    // orange rows
    below1Point2Coefficient: {
      count: 0,
      requireApproval: false,
    },
    // pink rows
    below70PercentOfPropositionPrice: {
      count: 0,
      requireApproval: false,
    },
    paymentTerms: {
      requireApproval: false,
    },
    sheetPeriod: {
      requireApproval: false,
    },
    valueAbove500: {
      requireApproval: false,
    },
    valueAbove200: {
      requireApproval: false,
    },
  };

  const askApproval = async () => {
    if (email && comment && approvalReasons) {
      const createApprovalRequestResponse =
        await createApprovalRequest(
          pricingSheetId,
          email,
          comment,
          approvalReasons,
        );

      if (createApprovalRequestResponse?.status === 200) {
        try {
          await updatePricingSheetById(pricingSheetId, {
            state: PricingSheetState.Pending,
            creatorComment: comment,
          });
          setNotification({
            type: 'SNACKBAR',
            duration: 4000,
            severity: 'success',
            message: 'Hinnaston hyväksymispyyntö lähetetty!',
          });
          history.push('/hinnastot');
        } catch (error) {
          console.error(error);
          setNotification({
            severity: 'error',
            message:
              'Hinnaston hyväksymispyynnön lähetys epäonnistui!',
          });
        }
      } else {
        setNotification({
          severity: 'error',
          message: 'Virhe!',
        });
      }
    } else {
      setNotification({
        type: 'SNACKBAR',
        duration: 4000,
        severity: 'error',
        message: 'Kirjoita sähköpostiosoite sekä perustelut!',
      });
    }
  };

  const validEmail = isValidEmail(email);

  return (
    <>
      <Box>
        <Typography fontWeight={'bold'}>
          {requiredActionLabels[requiredApprovalLevel]}
        </Typography>
      </Box>
      <FormControl
        fullWidth
        sx={{
          mt: 1,
        }}
      >
        <TextField
          sx={{ width: '100%', mb: 1 }}
          id="approval-email"
          label="Sähköpostiosoite"
          variant="outlined"
          type="email"
          value={email}
          error={
            !email ? false : !validEmail || !email ? true : false
          }
          helperText={
            !email
              ? ''
              : !validEmail || !email
                ? 'Varmista että sähköpostiosoite on oikein'
                : null
          }
          onChange={handleEmailChange}
        />

        <MultiLineComment
          label="Perustelut"
          placeholder="Lisää perustelut"
          handleCommentChange={handleCommentChange}
          required={true}
        />
      </FormControl>
      <Box>
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            disabled={
              !validEmail || !email || !comment ? true : false
            }
            variant="contained"
            onClick={askApproval}
            sx={{ height: 40 }}
          >
            Pyydä hyväksyntää
          </Button>
        </Box>
      </Box>
    </>
  );
};
